/**
 * Share Service
 * Handles different sharing strategies with clipboard fallback
 */

import { generateResultImage, generateShareText } from '@games/memorama/utils/shareMemoramaGenerator'
import { generatePuzzleResultImage, generatePuzzleShareText } from '@games/puzzle/utils/sharePuzzleGenerator'

/**
 * Share Service - Main coordinator
 * Detects capabilities and delegates to appropriate strategy
 */
export class ShareService {
  constructor () {
    this.capabilities = this.detectCapabilities()
  }

  /**
   * Detect browser and device sharing capabilities
   * @returns {Object} Available sharing features
   */
  detectCapabilities () {
    const hasWebShare = 'share' in navigator
    const hasWebShareFiles = hasWebShare && 'canShare' in navigator
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    return {
      webShare: hasWebShare,
      webShareFiles: hasWebShareFiles,
      mobile: isMobile,
      clipboard: 'clipboard' in navigator && 'writeText' in navigator.clipboard
    }
  }

  /**
   * Main share method - delegates to game-specific handlers
   * @param {Object} gameData - Game data with type discriminator
   * @param {string} gameData.type - Game type ('memorama' | 'puzzle')
   * @param {Object} translations - Translation object (t)
   * @param {string} theme - Current theme ('light' | 'dark')
   * @returns {Promise<boolean>} Success status
   */
  async share (gameData, translations, theme = 'light') {
    if (!gameData?.type) {
      console.error('[ShareService] gameData.type is required')
      throw new Error('gameData.type is required')
    }

    // Delegate to game-specific handler
    switch (gameData.type) {
      case 'memorama':
        return await this.shareMemorama(gameData, translations, theme)
      case 'puzzle':
        return await this.sharePuzzle(gameData, translations, theme)
      default:
        console.error(`[ShareService] Unsupported game type: ${gameData.type}`)
        throw new Error(`Unsupported game type: ${gameData.type}`)
    }
  }

  /**
   * Share Memorama game results
   * @param {Object} data - Memorama game data
   * @param {Object} data.playerData - Player information
   * @param {Object} data.gameData - Game information
   * @param {Object} translations - Translation object
   * @param {string} theme - Current theme
   * @returns {Promise<boolean>} Success status
   * @private
   */
  async shareMemorama (data, translations, theme) {
    let shareContent = null

    try {
      // Only proceed if mobile
      if (!this.capabilities.mobile) {
        throw new Error('Share feature only available on mobile devices')
      }

      const { playerData, gameData } = data

      // Generate share content
      const shareText = generateShareText(playerData, translations)
      const shareImage = await generateResultImage(playerData, gameData, translations, theme)

      shareContent = {
        title: translations.shared.share?.modalTitle || 'Memodiez - Mi resultado',
        text: shareText,
        url: 'https://memodiez.com'
      }

      // Try Web Share API, fallback to clipboard
      return await this.tryWebShareStrategy(shareContent, shareImage, translations)
    } catch (error) {
      console.error('[ShareService] Memorama share failed:', error)
      // Fallback: copy to clipboard
      const fallbackText = shareContent?.text || 'https://memodiez.com'
      return await this.tryCopyFallback(fallbackText, translations)
    }
  }

  /**
   * Share Puzzle game results
   * @param {Object} data - Puzzle game data
   * @param {string} data.difficulty - Difficulty level ('easy' | 'medium' | 'hard')
   * @param {string} data.imageSrc - Puzzle image source
   * @param {Object} translations - Translation object
   * @param {string} theme - Current theme
   * @returns {Promise<boolean>} Success status
   * @private
   */
  async sharePuzzle (data, translations, theme) {
    let shareContent = null

    try {
      // Only proceed if mobile
      if (!this.capabilities.mobile) {
        throw new Error('Share feature only available on mobile devices')
      }

      const { difficulty, imageSrc } = data

      // Generate share content
      const puzzleData = { difficulty, imageSrc }
      const shareText = generatePuzzleShareText(puzzleData, translations)
      const shareImage = await generatePuzzleResultImage(puzzleData, translations, theme)

      shareContent = {
        title: translations.shared.share?.modalTitle || 'Memodiez - Mi resultado',
        text: shareText,
        url: 'https://memodiez.com'
      }

      // Try Web Share API, fallback to clipboard
      return await this.tryWebShareStrategy(shareContent, shareImage, translations)
    } catch (error) {
      console.error('[ShareService] Puzzle share failed:', error)
      // Final fallback: copy to clipboard
      const fallbackText = shareContent?.text || 'https://memodiez.com'
      return await this.tryCopyFallback(fallbackText, translations)
    }
  }

  /**
   * Web Share API Strategy
   * @param {Object} shareContent - Base share data
   * @param {Blob} shareImage - Generated image blob
   * @param {Object} translations - Translations
   * @returns {Promise<boolean>} Success status
   * @private
   */
  async tryWebShareStrategy (shareContent, shareImage, translations) {
    try {
      // Check if files are supported and image can be shared
      if (this.capabilities.webShareFiles && shareImage) {
        const imageFile = new File([shareImage], 'memodiez-result.png', { type: 'image/png' })

        const shareDataWithFile = {
          ...shareContent,
          files: [imageFile]
        }

        // Verify the data can be shared
        if (navigator.canShare && navigator.canShare(shareDataWithFile)) {
          await navigator.share(shareDataWithFile)
          return true
        }
      }

      // Fallback: Share without image
      await navigator.share(shareContent)
      return true
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled - not an error
        return false
      }
      // Fall back to clipboard
      return await this.tryCopyFallback(shareContent.text, translations)
    }
  }

  /**
   * Copy to clipboard Fallback
   * @param {string} text - Text to copy
   * @param {Object} translations - Translations
   * @returns {Promise<boolean>} Success status
   * @private
   */
  async tryCopyFallback (text, translations) {
    try {
      if (this.capabilities.clipboard) {
        await navigator.clipboard.writeText(text)

        // Show success feedback
        const message = translations.shared.share?.copyFallback || 'Enlace copiado al portapapeles'
        // Trigger custom event for UI feedback
        const event = new CustomEvent('shareSuccess', { detail: { message } })
        window.dispatchEvent(event)

        return true
      }

      // No clipboard API available
      console.warn('[ShareService] Clipboard API not available')
      return false
    } catch (error) {
      console.error('[ShareService] Clipboard copy failed:', error)
      return false
    }
  }
}

// Singleton instance
export const shareService = new ShareService()
